// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { clearSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@keller/XDL/constants";
import Clock from "@keller/XDL/Icons/Clock";
import { color, xScreen } from "@keller/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedQuery?: string;
    goToUrl: (url: string) => void;
    extendedStyles?: SearchHistoryStyles;

    // <XNG-Changes>
    clearSearchHistoryCallback?: () => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLink?: LinkPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
}

// XNG-Changes: update style
export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        weight: "bold",
        size: 18,
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            text-align: left;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 0 20px 15px 0;
            ${xScreen("lte", constants.searchInput.breakpoints.lg)} {
                padding-left: 20px;
            }
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    icon: {
        src: Clock,
        size: 15,
        color: "primary",
        css: css`
            margin-right: 6px;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            size: 15,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            overflow: hidden;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            overflow: hidden;
            background-color: ${color("common.border")};
        `,
    },
    clearLink: {
        color: "common.ui1",
        typographyProps: {
            size: 13,
            weight: "bold",
            css: css`
                text-align: left;
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 8px;
            margin-bottom: 15px;
        `,
    },
};

const styles = searchHistoryStyles;

class SearchHistory extends React.Component<Props> {
    private readonly styles: SearchHistoryStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    clearLinkClickHandler = () => {
        clearSearchHistory();
        this.forceUpdate();

        // <XNG-Changes> callback to SearchInput to hide popup
        this.props.clearSearchHistoryCallback?.();
        // <XNG-Changes>
    };

    render() {
        if (!this.props.searchHistoryEnabled) {
            return null;
        }

        const searchHistory = getSearchHistory();
        if (searchHistory.length === 0) {
            return null;
        }

        const styles = this.styles;
        // XNG-Changes: update structure
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.headerText}>{translate("Search History")}</Typography>
                    <Link {...styles.clearLink} onClick={this.clearLinkClickHandler}>
                        {translate("Clear Search History")}
                    </Link>
                </StyledWrapper>
                {searchHistory.map(searchHistoryItem => (
                    <StyledWrapper {...styles.itemWrapper} key={searchHistoryItem.query}>
                        <Link
                            {...(this.props.focusedQuery === searchHistoryItem.query
                                ? styles.focusedLink
                                : styles.link)}
                            icon={{
                                iconProps: { ...styles.icon },
                            }}
                            onClick={() => {
                                if (searchHistoryItem.redirectUri) {
                                    this.props.goToUrl(searchHistoryItem.redirectUri);
                                } else {
                                    this.props.goToUrl(
                                        `/${this.props.searchPath}?query=${encodeURIComponent(
                                            searchHistoryItem.query,
                                        )}`,
                                    );
                                }
                            }}
                        >
                            {searchHistoryItem.query}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps)(SearchHistory);
